import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

export type ReductionColorTheme = 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'pink' | 'golden' | 'custom';

export interface ColorPreset {
  primary: string;
  secondary: string;
  shadow: string;
  badge: string;
}

@Component({
  selector: 'app-reduction-badge',
  templateUrl: './reduction-badge.component.html',
  standalone: false,
  styleUrls: ['./reduction-badge.component.scss']
})
export class ReductionBadgeComponent implements OnInit {
  @Input() reduction: number | null = null;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() showOffBadge: boolean = true;
  @Input() disabled: boolean = false;
  @Input() colorTheme: ReductionColorTheme = 'blue';
  @Input() customColor: string | null = null;
  @Input() customSecondaryColor: string | null = null;
  
  @Output() reductionClick = new EventEmitter<number>();

  private colorPresets: Record<ReductionColorTheme, ColorPreset> = {
    blue: {
      primary: '#1d5d9b',
      secondary: '#75c2f6',
      shadow: 'rgba(29, 93, 155, 0.4)',
      badge: '#1d5d9b'
    },
    green: {
      primary: '#2ed573',
      secondary: '#7bed9f',
      shadow: 'rgba(46, 213, 115, 0.4)',
      badge: '#2ed573'
    },
    red: {
      primary: '#ff4757',
      secondary: '#ff6b7a',
      shadow: 'rgba(255, 71, 87, 0.4)',
      badge: '#ff4757'
    },
    orange: {
      primary: '#ff6b35',
      secondary: '#ff8c42',
      shadow: 'rgba(255, 107, 53, 0.4)',
      badge: '#ff6b35'
    },
    purple: {
      primary: '#8b5cf6',
      secondary: '#a78bfa',
      shadow: 'rgba(139, 92, 246, 0.4)',
      badge: '#8b5cf6'
    },
    pink: {
      primary: '#ec4899',
      secondary: '#f472b6',
      shadow: 'rgba(236, 72, 153, 0.4)',
      badge: '#ec4899'
    },
    golden: {
      primary: '#f2c525',
      secondary: '#f4d03f',
      shadow: 'rgba(242, 197, 37, 0.4)',
      badge: '#f2c525'
    },
    custom: {
      primary: '#1d5d9b',
      secondary: '#75c2f6',
      shadow: 'rgba(29, 93, 155, 0.4)',
      badge: '#1d5d9b'
    }
  };

  currentColors: ColorPreset = this.colorPresets.blue;

  ngOnInit(): void {
    this.updateColors();
  }

  ngOnChanges(): void {
    this.updateColors();
  }

  private updateColors(): void {
    if (this.colorTheme === 'custom' && this.customColor) {
      this.currentColors = {
        primary: this.customColor,
        secondary: this.customSecondaryColor || this.customColor,
        shadow: this.hexToRgba(this.customColor, 0.4),
        badge: this.customColor
      };
    } else {
      this.currentColors = this.colorPresets[this.colorTheme];
    }
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  get reductionValue(): string {
    return this.reduction ? `${this.reduction}%` : '';
  }

  get sizeClass(): string {
    return `reduction-badge--${this.size}`;
  }

  get colorThemeClass(): string {
    return `reduction-badge--${this.colorTheme}`;
  }

  get customColorClass(): string {
    return this.customColor ? 'reduction-badge--custom' : '';
  }

  get dynamicStyles(): any {
    return {
      '--primary-color': this.currentColors.primary,
      '--secondary-color': this.currentColors.secondary,
      '--shadow-color': this.currentColors.shadow,
      '--badge-color': this.currentColors.badge
    };
  }

  onReductionClick(): void {
    if (!this.disabled && this.reduction) {
      this.reductionClick.emit(this.reduction);
    }
  }
}
